import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  coverageText,
  describeNote,
  excludedText,
  moneyText,
  movementParts,
  statementText,
} from "../lib/accountBreakdown";

/**
 * STARSEED-CAPMARKETS-325 — each of a member's partner accounts, separately.
 *
 * The Estimate card above this one is a single net figure, and it moves only
 * when money moves in or out. Members read it as a live balance and asked why
 * it had not changed in a month. This card says where the money in each
 * account is:
 *
 *   Moved into the pool   money paid in less money paid out, less the cash
 *                         still in the account. Updates the moment the partner
 *                         records a movement. The TOTAL is worked out for the
 *                         member as a whole, so money moved between their own
 *                         accounts cancels out.
 *   In the account now    the partner's live figure. $0.00 on a pooled account
 *                         is TRUE of the account and is never labelled a
 *                         balance (CAPMARKETS-190); it only appears beside the
 *                         pool figure.
 *   Last statement        the only figure here that includes trading results,
 *                         as of its date, with how it was obtained
 *                         (CAPMARKETS-115).
 *
 * States, each rendered distinctly because collapsing any pair says something
 * false about someone's money:
 *   undefined           not fetched — nothing
 *   null                could not load — says so, never a zero
 *   not_enabled         the operator switch is off for members — nothing
 *   no_accounts_mapped  nothing attached — nothing (the Estimate card says so)
 *   available           the card
 *
 * A total that covers only some accounts says how many and which it leaves out.
 * No trade data of any kind, no equity, and no profit or percentage computed
 * between two figures (CAPMARKETS-231, CAPMARKETS-310 AC5).
 */

const Figure = ({ testId, label, caption, text, note, detail }) => (
  <div data-testid={testId}>
    <div className="text-xs text-muted-foreground">{label}</div>
    {text ? (
      <div className="font-semibold">{text}</div>
    ) : (
      <div className="text-sm italic text-muted-foreground" data-testid={`${testId}-note`}>
        {note}
      </div>
    )}
    {detail ? (
      <div className="text-xs text-muted-foreground" data-testid={`${testId}-detail`}>
        {detail}
      </div>
    ) : null}
    {caption ? <div className="text-xs text-muted-foreground">{caption}</div> : null}
  </div>
);

const partialDetail = (total) =>
  [coverageText(total), excludedText(total)].filter(Boolean).join(" ") || null;

const AccountRow = ({ account }) => {
  const parts = movementParts(account.movements);
  return (
    <div
      data-testid="breakdown-row"
      data-account={account.account}
      className="rounded-lg border border-platinum/10 p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Account {account.account}</span>
        {account.retired ? (
          <Badge variant="outline" data-testid="breakdown-row-retired">Retired</Badge>
        ) : null}
      </div>

      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Figure
          testId="breakdown-row-pool"
          label="Moved into the pool"
          text={moneyText(account.in_pool)}
          note={describeNote(account.in_pool_note)}
        />
        <Figure
          testId="breakdown-row-account"
          label="In the account now"
          text={moneyText(account.in_account)}
          note={describeNote(account.in_account_note)}
        />
        <Figure
          testId="breakdown-row-statement"
          label="Last statement"
          text={statementText(account.statement)}
          note={describeNote("no_statement")}
        />
      </div>

      <p className="mt-2 text-xs text-muted-foreground" data-testid="breakdown-row-movements">
        {account.movements
          ? parts.map((p) => `${p.label} ${p.text}`).join(" · ")
          : describeNote("ledger_unavailable")}
      </p>
      {account.retired ? (
        <p className="text-xs text-muted-foreground">
          Retired: its movements still count, and its statement no longer counts toward
          your total.
        </p>
      ) : null}
    </div>
  );
};

const AccountBreakdownCard = ({ breakdown }) => {
  if (breakdown === undefined) return null;
  if (breakdown === null) {
    return (
      <Card className="bg-surface border-platinum/10" data-testid="breakdown-unavailable">
        <CardContent className="p-4 text-sm text-muted-foreground">
          Your accounts could not be loaded right now. This is not a statement that you
          have none. Please try again shortly.
        </CardContent>
      </Card>
    );
  }
  if (breakdown.status !== "available") return null;

  const totals = breakdown.totals || {};
  const sources = breakdown.sources || {};
  const accounts = breakdown.accounts || [];
  const pool = totals.in_pool || {};
  const inAccount = totals.in_account || {};
  const statement = totals.statement;
  const liveRead = [sources.ledger?.status, sources.balances?.status].some(
    (s) => s === "ok" || s === "partial",
  );

  return (
    <Card className="bg-surface border-platinum/10" data-testid="account-breakdown-card">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
          Your accounts
          {breakdown.preview ? (
            <Badge variant="outline" data-testid="breakdown-preview">
              Preview — members do not see this yet
            </Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Each partner account on its own line. Money paid in and out shows here as soon
          as the partner records it. Trading results are only in the last statement,
          which changes when a new statement is recorded.
        </p>

        {sources.ledger?.status === "unavailable" ? (
          <p className="text-sm text-muted-foreground" data-testid="breakdown-source-ledger">
            {describeNote("ledger_unavailable")}
          </p>
        ) : null}
        {sources.ledger?.status === "partial" ? (
          <p className="text-sm text-muted-foreground" data-testid="breakdown-source-ledger">
            {describeNote("ledger_partial")}
          </p>
        ) : null}
        {sources.balances?.status === "unavailable" ? (
          <p className="text-sm text-muted-foreground" data-testid="breakdown-source-balances">
            {describeNote("balance_unavailable")}
          </p>
        ) : null}

        <div
          className="grid grid-cols-1 gap-3 rounded-lg border border-platinum/10 p-3 sm:grid-cols-3"
          data-testid="breakdown-totals"
        >
          <Figure
            testId="breakdown-total-pool"
            label={`Moved into the pool, across ${accounts.length} account${accounts.length === 1 ? "" : "s"}`}
            text={moneyText(pool.value)}
            note={pool.note ? describeNote(pool.note) : excludedText(pool) || "Not available right now."}
            caption="Money paid in less money paid out, before trading results."
          />
          <Figure
            testId="breakdown-total-account"
            label="In the accounts now"
            text={moneyText(inAccount.value)}
            note={excludedText(inAccount) || "Not available right now."}
            detail={inAccount.value === null || inAccount.value === undefined
              ? null
              : partialDetail(inAccount)}
            caption="Live from the partner. Money in the pool is not in the account."
          />
          <Figure
            testId="breakdown-total-statement"
            label="Last statement"
            text={statement && statement.balance !== null && statement.balance !== undefined
              ? statementText(statement)
              : null}
            note={excludedText(statement) || describeNote("no_statement")}
            detail={statement && statement.balance !== null && statement.balance !== undefined
              ? partialDetail(statement)
              : null}
            caption="Includes trading results, as of its date."
          />
        </div>

        {breakdown.includes_unexplained_adjustments ? (
          <p className="text-xs text-muted-foreground" data-testid="breakdown-adjustments">
            Includes partner adjustments that are still being reconciled, so these figures
            may change.
          </p>
        ) : null}

        <div className="space-y-2">
          {accounts.map((a) => (
            <AccountRow key={a.account} account={a} />
          ))}
        </div>

        {liveRead && breakdown.as_of ? (
          <p className="text-xs text-muted-foreground" data-testid="breakdown-asof">
            Live figures read {new Date(breakdown.as_of).toLocaleString()}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default AccountBreakdownCard;
